// @ts-nocheck
import { getCookie } from "@insite/client-framework/Common/Cookies";
import { convertDateToDateOnlyString } from "@insite/client-framework/Common/Utilities/DateUtilities";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getCurrentShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import {
    ExcludeFromNavigation,
    ExcludeFromSignInRequired,
    HideFooter,
    HideFromSearchEngines,
    HideFromSiteSearch,
    HideHeader,
    HorizontalRule,
    MetaDescription,
    MetaKeywords,
    OpenGraphImage,
    OpenGraphTitle,
    OpenGraphUrl,
    StructuredPageData,
} from "@insite/client-framework/Types/FieldDefinition";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import Page from "@insite/mobius/Page";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import { GetShipmentsApiParameter } from "@store/Framework/Services/ShipmentService";
import loadShipments from "@store/Framework/Store/Greif/Data/Shipments/Handlers/LoadShipments";
import {
    getShipmentsDataView,
    ShipmentsDataViewContext,
} from "@store/Framework/Store/Greif/Data/Shipments/ShipmentsSelectors";
import loadShipmentStatuses from "@store/Framework/Store/Greif/Data/ShipmentStatusMapping/Handlers/LoadShipmentStatuses";
import { getShipmentStatusesDataView } from "@store/Framework/Store/Greif/Data/ShipmentStatusMapping/ShipmentStatusesSelectors";
import updateSearchFields from "@store/Framework/Store/Greif/Pages/ShipmentHistory/Handlers/UpdateSearchFields";
import qs from "qs";
import * as React from "react";
import { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const { getShipmentsParameter, getShipmentStatusesParameter } = state.greif.pages.shipmentHistory;

    return {
        getShipmentsParameter,
        getShipmentStatusesParameter,
        settings: getSettingsCollection(state),
        shipmentsDataView: getShipmentsDataView(state, getShipmentsParameter),
        shouldLoadShipmentStatuses: !getShipmentStatusesDataView(state).value,
        location: getLocation(state),
        currentShipToValue: getCurrentShipToState(state).value,
    };
};

const mapDispatchToProps = {
    loadShipments,
    loadShipmentStatuses,
    updateSearchFields,
};

type Props = PageProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

const ShipmentHistoryPage = ({
    id,
    history,
    location,
    settings,
    shipmentsDataView,
    shouldLoadShipmentStatuses,
    getShipmentsParameter,
    getShipmentStatusesParameter,
    currentShipToValue,
    loadShipments,
    loadShipmentStatuses,
    updateSearchFields,
}: Props) => {
    let firstLoad = false;

    const getShipmentsApiParameter = parseQueryString<GetShipmentsApiParameter>(location.search);

    useEffect(() => {
        firstLoad = true;
        const pageSizeCookie = getCookie("ShipmentHistory-PageSize");
        const pageSize = pageSizeCookie ? parseInt(pageSizeCookie, 10) : undefined;
        if (location.search) {
            if (pageSize) {
                getShipmentsApiParameter.pageSize = pageSize;
            }
            updateSearchFields({ ...getShipmentsApiParameter, type: "Replace" });
        } else if (settings.orderSettings.lookBackDays > 0) {
            const fromDate = new Date(Date.now() - settings.orderSettings.lookBackDays * 60 * 60 * 24 * 1000);
            updateSearchFields({
                ...getShipmentsApiParameter,
                fromDate: convertDateToDateOnlyString(fromDate),
                type: "Initialize",
                pageSize,
                customerSequence: currentShipToValue?.customerSequence,
            });
        } else {
            updateSearchFields({
                ...getShipmentsApiParameter,
                type: "Initialize",
                pageSize,
                customerSequence: currentShipToValue?.customerSequence,
            });
        }
    }, []);

    useEffect(() => {
        if (!firstLoad) {
            const queryString = qs.stringify(getShipmentsParameter);
            history.replace(`${location.pathname}${queryString !== "" ? `?${queryString}` : ""}`);
        }
    }, [getShipmentsParameter]);

    useEffect(() => {
        if (!shipmentsDataView.value && !shipmentsDataView.isLoading) {
            loadShipments(getShipmentsParameter);
        } else if (
            !shipmentsDataView.isLoading &&
            shipmentsDataView.value?.length === 0 &&
            getShipmentsParameter.page
        ) {
            updateSearchFields({ page: getShipmentsParameter.page - 1 });
        }
    });

    useEffect(() => {
        if (shouldLoadShipmentStatuses) {
            loadShipmentStatuses(getShipmentStatusesParameter);
        }
    }, [shouldLoadShipmentStatuses]);

    return (
        <Page
            data-test-selector="shipmentHistory"
            css={css`
                position: relative;
                flex-grow: 1;
            `}
        >
            <ShipmentsDataViewContext.Provider value={shipmentsDataView}>
                <Zone contentId={id} zoneName="Content" />
            </ShipmentsDataViewContext.Provider>
        </Page>
    );
};

const pageModule: PageModule = {
    component: withHistory(connect(mapStateToProps, mapDispatchToProps)(ShipmentHistoryPage)),
    definition: {
        hasEditableTitle: true,
        hasEditableUrlSegment: true,
        pageType: "System",
        fieldDefinitions: [
            MetaKeywords,
            MetaDescription,
            OpenGraphTitle,
            OpenGraphUrl,
            OpenGraphImage,
            StructuredPageData,
            {
                ...HorizontalRule,
                sortOrder: 199,
            },
            HideHeader,
            HideFooter,
            HideFromSearchEngines,
            HideFromSiteSearch,
            ExcludeFromNavigation,
            ExcludeFromSignInRequired,
        ],
    },
};

export default pageModule;
